import { useEffect, useRef, useState } from 'react';

import { DatePicker } from '@components/datepicker';
import {
  FieldType,
  FieldTypeKind,
  formatDateWithoutTimezone,
  KENNITALA_LENGTH,
  SHORT_TEXT_MAX_LENGTH,
} from '@components/form/v2';
import {
  getNumberRestrictionError,
  getNumberRestrictionViolation,
  NumberField,
  NumberRestrictions,
} from '@components/number-field';
import { PhoneField } from '@components/phone-field';
import { getManualDefaultValue } from '@components/text-editors/addons/field-mapping';
import { DateManualRestrictions } from '@components/text-editors/field-mapping-sidebar';
import { TextField } from '@components/text-field';
import { TextArea } from '@components/textarea';
import { getScopedTranslate } from '@locale/translate';
import { parseDate } from '@utils/date';
import { isValidSsn, validateEmail } from '@utils/validators';

import { useFormBuilderContext } from './context';
import { DefaultValueSection } from './default-value-section';
import { StateField } from './state';

type DefaultValueInputKind =
  | 'input-text-small'
  | 'input-text-large'
  | 'input-kennitala'
  | 'input-number'
  | 'input-date'
  | 'input-email'
  | 'input-phone';

const DEFAULT_VALUE_INPUT_KINDS: ReadonlySet<FieldTypeKind> = new Set<DefaultValueInputKind>([
  'input-text-small',
  'input-text-large',
  'input-kennitala',
  'input-number',
  'input-date',
  'input-email',
  'input-phone',
]);

type DefaultValueInputField = Extract<StateField, { kind: DefaultValueInputKind }>;

// Narrows `field` to the scalar-kind union so `createChangeHandler` can resolve
// `defaultValue`'s type — `keyof StateField` across the full union only
// includes properties every kind shares (id/kind/builder/builderProps).
const isDefaultValueInputField = (field: StateField): field is DefaultValueInputField =>
  DEFAULT_VALUE_INPUT_KINDS.has(field.kind);

/**
 * Kinds whose control renders a manually-typed `mappedDefaultValue` inline. For
 * every other kind the builder shows it as a pill instead
 * (`DefaultValueMappingDisplay`) and `dynamic-fields.tsx` ignores it at runtime,
 * so surfacing it in the control would both double up with the pill and show a
 * value the rendered form doesn't.
 */
export const INLINE_MANUAL_DEFAULT_KINDS: ReadonlySet<string> = new Set([
  'input-number',
  'input-date',
  'input-email',
  'input-phone',
]);

export const getBuilderDisplayDefault = (field: StateField | FieldType): string | undefined => {
  if (INLINE_MANUAL_DEFAULT_KINDS.has(field.kind)) {
    const mapped = getManualDefaultValue(
      'mappedDefaultValue' in field ? field.mappedDefaultValue : undefined
    );
    if (mapped != null && mapped !== '') return mapped;
  }
  const staticDefault = 'defaultValue' in field ? field.defaultValue : undefined;
  if (typeof staticDefault === 'string') return staticDefault || undefined;
  if (typeof staticDefault === 'number' && Number.isFinite(staticDefault)) {
    return String(staticDefault);
  }
  return undefined;
};

/**
 * Mirrors `clearManualDateDefaultIfInvalid` (`default-value-mapping.tsx`) but
 * reads and writes the plain string in `defaultValue` rather than a
 * `mappedDefaultValue`.
 */
export const clearStaticDateDefaultIfInvalid = (
  defaultValue: string | undefined,
  restrictions: DateManualRestrictions | undefined,
  setDefaultValue: (value: string) => void
): void => {
  if (!restrictions || !defaultValue) return;
  const date = parseDate(defaultValue);
  if (Number.isNaN(date.getTime())) return;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const violates =
    (restrictions.disablePastDates && date < startOfToday) ||
    (restrictions.disableFutureDates && date > startOfToday) ||
    (restrictions.minDate && date < restrictions.minDate) ||
    (restrictions.maxDate && date > restrictions.maxDate);
  if (violates) {
    setDefaultValue('');
  }
};

/** Both default-value controls derive their restrictions here, so they agree. */
export const getNumberRestrictions = (field: {
  isDecimal: boolean;
  allowNegative: boolean;
  restrictMin: boolean;
  min: number | null;
  restrictMax: boolean;
  max: number | null;
}): NumberRestrictions => ({
  min: field.restrictMin && field.min != null ? field.min : undefined,
  max: field.restrictMax && field.max != null ? field.max : undefined,
  disableNegative: !field.allowNegative,
  disableDecimal: !field.isDecimal,
});

/**
 * Mirrors `clearMappedNumberDefaultIfInvalid` (`default-value-mapping.tsx`)
 * minus its variable-type branch — a static default can never hold a
 * journey-variable mapping.
 */
export const clearStaticNumberDefaultIfInvalid = (
  defaultValue: number | null | undefined,
  restrictions: NumberRestrictions,
  setDefaultValue: (value: number | null) => void
): void => {
  if (getNumberRestrictionViolation(defaultValue, restrictions)) {
    setDefaultValue(null);
  }
};

type NumberStateField = Extract<StateField, { kind: 'input-number' }>;

const NumberDefaultValueInput = ({ field }: { field: NumberStateField }) => {
  const actions = useFormBuilderContext().store.actions;
  const setDefault = actions.createChangeHandler(field, 'defaultValue');
  const restrictions = getNumberRestrictions(field);

  const lastWrittenRef = useRef<number | null | undefined>(field.defaultValue);
  const [revision, setRevision] = useState(0);
  // What the box shows, which is not what's stored when it breaks a restriction:
  // the number stays visible with the error, `defaultValue` is cleared.
  const shownValueRef = useRef<number | null>(field.defaultValue ?? null);
  // Checked on every keystroke: the value isn't stored while it breaks a
  // restriction, so the error is the only sign of that, and closing the field
  // editor would take a blur-only error with it before it was ever seen.
  const [error, setError] = useState(() =>
    getNumberRestrictionError(field.defaultValue, restrictions)
  );

  // Keeps `defaultValue === (violates(shown) ? null : shown)`. A stored value
  // that already matches is the properties tab clearing the same violation, not
  // an external edit — adopting it would blank the box and the error with it.
  const { min, max, disableNegative, disableDecimal } = restrictions;
  useEffect(() => {
    const shown = shownValueRef.current;
    const violation = getNumberRestrictionViolation(shown, restrictions);
    const expected = violation ? null : shown;

    if (field.defaultValue !== lastWrittenRef.current && field.defaultValue !== expected) {
      lastWrittenRef.current = field.defaultValue;
      shownValueRef.current = field.defaultValue ?? null;
      setError(undefined);
      setRevision((current) => current + 1);
      return;
    }

    setError(getNumberRestrictionError(shown, restrictions));
    if (expected === lastWrittenRef.current) return;
    lastWrittenRef.current = expected;
    setDefault(expected);
    // `restrictions` is rebuilt every render; depend on its values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.defaultValue, min, max, disableNegative, disableDecimal]);

  return (
    <NumberField
      key={`default-number-${field.id}-${revision}-${String(field.isDecimal)}`}
      hideLabel
      label={t('defaultValue')}
      isDecimal={field.isDecimal}
      min={min}
      max={max}
      defaultValue={field.defaultValue ?? undefined}
      error={error}
      onChange={(value) => {
        const next = value ?? null;
        shownValueRef.current = next;
        setError(getNumberRestrictionError(next, restrictions));
        const stored = getNumberRestrictionViolation(next, restrictions) ? null : next;
        lastWrittenRef.current = stored;
        setDefault(stored);
      }}
      onBlur={() => setError(getNumberRestrictionError(shownValueRef.current, restrictions))}
    />
  );
};

type TextDefaultValueInputProps = {
  value: string;
  onChange: (value: string) => void;
  maxChars?: number;
  /** Applied to typed input before it is stored. */
  transform?: (value: string) => string;
  /** Returns an error message, or undefined when the value is acceptable. */
  validate?: (value: string) => string | undefined;
};

const TextDefaultValueInput = ({
  value,
  onChange,
  maxChars,
  transform,
  validate,
}: TextDefaultValueInputProps) => {
  const initialError = value ? validate?.(value) : undefined;
  const [error, setError] = useState(initialError);
  // A transformed box is controlled, so what the transform drops — letters typed
  // or pasted into a kennitala — never appears in it at all.
  const [transformedText, setTransformedText] = useState(value);

  // As in `NumberDefaultValueInput`: a value the rendered form would reject is
  // never stored, so a default loaded from an older form is dropped on sight.
  useEffect(() => {
    if (initialError) onChange('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TextField
      hideLabel
      label={t('defaultValue')}
      {...(transform ? { value: transformedText } : { defaultValue: value })}
      maxChars={maxChars}
      onChange={(newValue) => {
        const current = transform ? transform(newValue) : newValue;
        setTransformedText(current);
        const message = current ? validate?.(current) : undefined;
        setError(message);
        // The text stays in the box with its error; only a value the field would
        // accept reaches the form.
        onChange(message ? '' : current);
      }}
      onBlur={(event) => {
        const current = transform ? transform(event.target.value) : event.target.value;
        setError(current ? validate?.(current) : undefined);
      }}
      error={error}
    />
  );
};

const DefaultValueControl = ({ field }: { field: DefaultValueInputField }) => {
  const setDefault = useFormBuilderContext().store.actions.createChangeHandler(
    field,
    'defaultValue'
  );

  switch (field.kind) {
    case 'input-text-small':
      return (
        <TextDefaultValueInput
          value={field.defaultValue ?? ''}
          onChange={setDefault}
          maxChars={SHORT_TEXT_MAX_LENGTH}
        />
      );

    case 'input-text-large':
      return (
        <TextArea
          hideLabel
          label={t('defaultValue')}
          defaultValue={field.defaultValue ?? ''}
          onChange={setDefault}
        />
      );

    case 'input-email':
      return (
        <TextDefaultValueInput
          value={field.defaultValue ?? ''}
          onChange={setDefault}
          validate={(value) => (validateEmail(value) ? undefined : t('invalidEmail'))}
        />
      );

    case 'input-kennitala':
      return (
        <TextDefaultValueInput
          value={field.defaultValue ?? ''}
          onChange={setDefault}
          maxChars={KENNITALA_LENGTH}
          transform={(value) => value.replace(/\D/g, '')}
          validate={(value) => (isValidSsn(value) ? undefined : t('invalidKennitala'))}
        />
      );

    case 'input-phone':
      return (
        <PhoneField
          hideLabel
          label={t('defaultValue')}
          defaultValue={field.defaultValue ?? undefined}
          onChange={setDefault}
        />
      );

    case 'input-number':
      return <NumberDefaultValueInput field={field} />;

    case 'input-date':
      return (
        <DatePicker
          hideLabel
          label={t('defaultValue')}
          placeholder={field.placeholder}
          value={field.defaultValue || null}
          onChange={(date) => setDefault(date ? formatDateWithoutTimezone(date) : '')}
          disablePastDates={field.dateRestrictionType === 'disablePast'}
          disableFutureDates={field.dateRestrictionType === 'disableFuture'}
          minDate={
            field.dateRestrictionType === 'range' && field.dateRangeStart
              ? parseDate(field.dateRangeStart)
              : undefined
          }
          maxDate={
            field.dateRestrictionType === 'range' && field.dateRangeEnd
              ? parseDate(field.dateRangeEnd)
              : undefined
          }
        />
      );
  }
};

export const DefaultValueInput = ({ field }: { field: StateField }) => {
  if (!isDefaultValueInputField(field)) {
    return null;
  }

  if (field.builderProps?.canEditDefaultValue === false) {
    return null;
  }

  return (
    <DefaultValueSection>
      <DefaultValueControl field={field} />
    </DefaultValueSection>
  );
};

const { t } = getScopedTranslate({
  en: {
    defaultValue: 'Default value',
    invalidEmail: 'Enter a valid email address',
    invalidKennitala: 'Enter a valid kennitala',
  },
});
